import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccountsCrumb } from './lib/usePhoneCrumb';
import { Card, Chip, DChip, TabBar } from '@/components/kit';
import apiClient from '@/services/apiClient';
import { useNarrowViewport, useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useTenantStore } from '@/stores/useTenantStore';
import { PeriodRow } from './components/PeriodRow';
import { exportXlsx } from './lib/exportXlsx';
import { exportPdf } from './lib/exportPdf';
import { useAllBills } from './lib/useBills';
import {
  amount,
  monthName,
  thisPeriod,
  useAccountsSummary,
  usePayrollRun,
  useProfitAndLoss,
  useReceivables,
  type AccountsSummary,
} from './lib/useAccounts';
import { AccountantMenu } from './AccountantMenu';
import { Bar, StackBar } from '../clinic-manager/components/Charts';
import { ReportMonthDefault, useReportMonth } from '../clinic-manager/lib/useReportMonth';
import { ManagerInsurancePage } from '../clinic-manager/reports/ManagerInsurancePage';
import { ManagerEditsPage } from '../clinic-manager/reports/ManagerEditsPage';
import { ManagerChartsPage } from '../clinic-manager/reports/ManagerChartsPage';
import { ManagerTrendsPage } from '../clinic-manager/reports/ManagerTrendsPage';

/**
 * Financial reports — `g_reports` (12), `g_a6`. THE MANAGER'S MONEY REPORTS,
 * READ-ONLY, as five tabs.
 *
 * Revenue & collections is drawn here from the books the accountant already
 * reads (the month's collected, the bills' own totals, the payout run's base
 * per doctor); the other four tabs ARE the clinic manager's report screens,
 * mounted rather than copied, so two screens can never quote different
 * figures for the same month.
 */

type Tab = 'revenue' | 'insurance' | 'edits' | 'charts' | 'trends';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'revenue', label: 'Revenue & collections' },
  { key: 'insurance', label: 'Insurance & receivables' },
  { key: 'edits', label: 'Discounts, refunds & edits' },
  { key: 'charts', label: 'The month in charts' },
  { key: 'trends', label: 'Trends' },
];

const METHODS: Array<{ key: string; label: string; colour: string }> = [
  { key: 'cash', label: 'Cash', colour: 'var(--brand-500)' },
  { key: 'card', label: 'Card', colour: 'var(--brand-300)' },
  { key: 'online', label: 'Online', colour: 'var(--info-600)' },
  { key: 'insurance', label: 'Insurance', colour: 'var(--beige-500)' },
  { key: 'bank_transfer', label: 'Transfer', colour: 'var(--ink-400)' },
  { key: 'check', label: 'Cheque', colour: 'var(--ink-300)' },
];

const major = (v: unknown) => Math.round(Number(v ?? 0) * 100);

export function ReportsPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = TABS.some((t) => t.key === raw) ? (raw as Tab) : 'revenue';
  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    if (next === 'revenue') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
  };

  usePageMeta({ title: 'Financial reports' });
  return (
    <ReportMonthDefault.Provider value={thisPeriod()}>
      {tab === 'revenue' ? (
        <RevenueTab tabs={<ReportTabs tab={tab} onTab={setTab} />} />
      ) : (
        <>
          <div className="maie desk acc-root" style={{ minHeight: 0, height: 'auto', paddingBottom: 0 }}>
            <div className="dcontent" style={{ paddingBottom: 0 }}>
              <ReportTabs tab={tab} onTab={setTab} />
            </div>
          </div>
          {tab === 'insurance' ? <ManagerInsurancePage /> : null}
          {tab === 'edits' ? <ManagerEditsPage /> : null}
          {tab === 'charts' ? <ManagerChartsPage /> : null}
          {tab === 'trends' ? <ManagerTrendsPage /> : null}
        </>
      )}
    </ReportMonthDefault.Provider>
  );
}

function ReportTabs({ tab, onTab }: { tab: Tab; onTab: (t: Tab) => void }) {
  return <TabBar label="Financial reports" items={TABS} active={tab} onSelect={onTab} />;
}

const back = (period: string, by: number) => {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y, m - 1 - by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

function RevenueTab({ tabs }: { tabs: React.ReactNode }) {
  const narrow = useNarrowViewport();
  const go = useNavigate();
  const { month: period, setMonth: setPeriod } = useReportMonth();
  const [query, setQuery] = useState('');
  const clinic = useTenantStore((s) => s.publicConfig)?.displayName ?? 'Clinic';

  const { data: summary } = useAccountsSummary(period);
  const { data: run } = usePayrollRun(period);
  const { data: bills } = useAllBills();
  const { data: pnl } = useProfitAndLoss(period);
  const { data: owed } = useReceivables();

  const months = [back(period, 2), back(period, 1), period];
  const trio = useQueries({
    queries: months.map((p) => ({
      queryKey: ['accounts', 'summary', p],
      staleTime: 60_000,
      /* A month that failed to load must never read as a month with no money in it. */
      retry: 2,
      queryFn: async (): Promise<AccountsSummary | null> => {
        const res = await apiClient.get('/accounts/summary', { params: { period: p } });
        return ((res.data as { data?: AccountsSummary })?.data ?? res.data) as AccountsSummary;
      },
    })),
  });

  const inMonth = useMemo(
    () => (bills ?? []).filter((b) => (b.date ?? '').startsWith(period) && b.status !== 'cancelled'),
    [bills, period],
  );
  const billed = inMonth.reduce((n, b) => n + major(b.total), 0);
  const collected = summary?.collectedMinor ?? 0;
  const rate = billed ? Math.round((collected / billed) * 100) : null;

  const mix = METHODS.map((m) => ({
    key: m.key,
    label: m.label,
    colour: m.colour,
    value: inMonth.filter((b) => b.paymentMethod === m.key).reduce((n, b) => n + major(b.amountPaid), 0),
  }));

  const doctors = (run?.lines ?? [])
    /* Only a deal that takes a SHARE has a base: a per-visit base is a visit count,
       and a doctor with no deal set has nothing computed on what they collected. */
    .filter((l) => l.lane === 'doctors' && l.baseMinor > 0 && ['percent', 'hybrid', 'commission'].includes(l.dealKind) && /%/.test(l.dealSummary ?? ''))
    .filter((l) => !query.trim() || l.personName.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => b.baseMinor - a.baseMinor);
  const doctorMax = Math.max(1, ...doctors.map((d) => d.baseMinor));
  const trioMax = Math.max(1, ...trio.map((q) => q.data?.collectedMinor ?? 0));
  const insurers = (owed ?? []).filter((r) => r.kind === 'insurer');
  const age = [
    { label: '0–30 days', v: insurers.reduce((n, r) => n + r.current, 0), tone: 'warn' as const },
    { label: '31–60 days', v: insurers.reduce((n, r) => n + r.d30, 0), tone: 'warn' as const },
    { label: '60+ days', v: insurers.reduce((n, r) => n + r.d60 + r.d90, 0), tone: 'danger' as const },
  ];
  const ageMax = Math.max(1, ...age.map((a) => a.v));
  const outs = (pnl?.lines ?? []).filter((l) => l.kind === 'out');
  const outMax = Math.max(1, ...outs.map((l) => Math.abs(l.amountMinor)));

  const rowsOut = () => [
    ['Collected', (collected / 100).toFixed(2)],
    ['Billed', (billed / 100).toFixed(2)],
    ['Collection rate', rate === null ? '' : `${rate}%`],
    ...doctors.map((d) => [`Doctor — ${d.personName}`, (d.baseMinor / 100).toFixed(2)]),
  ];

  const crumb = useAccountsCrumb('Accounts', 'Financial reports');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <AccountantMenu door="reports" onClose={close} />,
      title: 'Financial reports',
      sub: (
        <>
          the manager&rsquo;s money reports <Chip kind="outline" small>read-only</Chip>
        </>
      ),
      search: 'Search a report, a month…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export .xlsx',
        onClick: () => void exportXlsx(`financial-report-${period}`, [{ name: 'Financial report', headers: ['Figure', 'Amount'], rows: rowsOut() }]),
      },
      secondary: {
        label: 'Branded PDF',
        onClick: () =>
          exportPdf(`financial-report-${period}`, clinic, `Revenue & collections — ${monthName(period)} ${period.slice(0, 4)}`, ['Figure', 'Amount'], rowsOut()),
      },
    },
    [period, collected, billed, doctors.length, query],
  );

  const kpis = (
    <div className="acc-stats" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
      <div className="card" style={{ margin: 0 }}>
        <p className="t-ovl mut">Collected</p>
        <p className="t-dxl grn mt8">{amount(collected)}</p>
      </div>
      <div className="card" style={{ margin: 0 }}>
        <p className="t-ovl mut">Billed</p>
        <p className="t-dxl mt8">{amount(billed)}</p>
      </div>
      <div className="card" style={{ margin: 0 }}>
        <p className="t-ovl mut">{narrow ? 'Rate' : 'Collection rate'}</p>
        <p className="t-dxl mt8">{rate === null ? '—' : `${rate}%`}</p>
      </div>
    </div>
  );

  const revenueCard = (
    <Card>
      <p className="t-ovl mut">Revenue — last 3 months</p>
      <div className="mt8">
        {months.map((p, i) => (
          <Bar
            key={p}
            label={monthName(p)}
            value={trio[i]?.data?.collectedMinor ?? 0}
            max={trioMax}
            display={trio[i]?.data ? amount(trio[i].data!.collectedMinor) : '—'}
            tone={i === 2 ? 'brand' : 'soft'}
          />
        ))}
      </div>
    </Card>
  );

  const doctorCard = (
    <Card>
      <p className="t-ovl mut">By doctor — {monthName(period)} · collected (base of a % deal)</p>
      <div className="mt8">
        {doctors.length ? (
          doctors.map((d, i) => (
            <Bar key={d.personId} label={d.personName} value={d.baseMinor} max={doctorMax} display={amount(d.baseMinor)} tone={i === 0 ? 'brand' : 'soft'} />
          ))
        ) : (
          <p className="t-sec mut">No doctor has a percentage base in {monthName(period)}.</p>
        )}
      </div>
      <p className="t-cap mut mt8">
        what each percentage payout is computed on — the base column in People we pay; salaried and
        per-visit doctors have no base at all
      </p>
    </Card>
  );

  const mixCard = (
    <Card>
      <p className="t-ovl mut">Payment method mix</p>
      <StackBar slices={mix} />
    </Card>
  );

  const againstCard = (
    <Card>
      <p className="t-ovl mut">Collected against billed</p>
      <div className="mt8">
        <Bar label="Billed" value={billed} max={Math.max(billed, collected, 1)} display={amount(billed)} tone="soft" />
        <Bar label="Collected" value={collected} max={Math.max(billed, collected, 1)} display={amount(collected)} tone="brand" />
      </div>
      <p className="t-cap mut mt8">
        {rate === null ? 'nothing was billed' : `${rate}%`} — the gap is what the desk and the insurers still owe
      </p>
    </Card>
  );

  const readOnly = (
    <Card kind="accent">
      <p className="t-sec brand">
        Read-only, like everything here — the numbers come from the books themselves. The manager
        owns these reports; the accountant reads and exports them.
      </p>
    </Card>
  );

  return (
    <div className="maie desk acc-root">
      <PeriodRow period={period} onChange={setPeriod} branches={summary?.branches} />
      <div className="dcontent scroll">
        {narrow ? null : tabs}
        {kpis}
        {narrow ? (
          <>
            {revenueCard}
            {doctorCard}
            <Card>
              <p className="t-ovl mut">Insurance — what&rsquo;s still out ({amount(summary?.insurersOweMinor ?? 0)})</p>
              <div className="mt8">
                {age.map((a) => (
                  <Bar key={a.label} label={a.label} value={a.v} max={ageMax} display={amount(a.v)} tone={a.tone} />
                ))}
              </div>
            </Card>
            <Card>
              <p className="t-ovl mut">Expenses — {monthName(period)} by category</p>
              <div className="mt8">
                {outs.length ? (
                  outs.map((l, i) => (
                    <Bar key={l.label} label={l.label} value={Math.abs(l.amountMinor)} max={outMax} display={amount(Math.abs(l.amountMinor))} tone={i === 0 ? 'brand' : 'soft'} />
                  ))
                ) : (
                  <p className="t-sec mut">Nothing was spent in {monthName(period)}.</p>
                )}
              </div>
            </Card>
            {mixCard}
          </>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
            <div style={{ minWidth: 0 }}>
              {revenueCard}
              {doctorCard}
            </div>
            <div style={{ minWidth: 0 }}>
              {mixCard}
              {againstCard}
              {readOnly}
            </div>
          </div>
        )}
        {narrow ? readOnly : null}
        <p className="t-sec mut mt12">
          Visits &amp; doctors and Patient sources stay manager-only — operations and marketing, not books.
        </p>
        {!narrow ? (
          <p className="mt12">
            <DChip onClick={() => go('/clinic-manager/reports')}>Open Clinic manager ▸ Reports</DChip>
          </p>
        ) : null}
      </div>
    </div>
  );
}
